import { computed } from "vue"
import { apiClient } from "@/services/api"
import { itemKindRights } from "@/services/entityTypes"
import { discounted, tierPrice } from "@/services/supplierEffectivePrice"
import { isEntitled, isUnreleased } from "@/services/entitlements"
import { FEATURE_SETS } from "@/services/featureSet"
import { useSupplierStrict } from "@/services/supplierMode"
import { comparisonUnit, convertUnitPrice, rowUnitOf } from "@/services/machinePurchaseUnits"
import { currenciesOf } from "@/services/priceCurrencies"

// The view over all suppliers, the Vergleich (docs/suppliers.md, "Auswertungen"). Plain
// requests, as `supplierPrices.js`: each view loads what it shows. The server answers 403 FEATURE_NOT_ENTITLED
// without SUPPLIER_INSIGHTS and 409 SUPPLIER_SETUP_REQUIRED before the setup.
//
// The navigation reads `insightKinds` while the app starts, before Pinia: nothing imported here may use a store at
// load (`number.js`, `unit.js` and `priceMap.js` do), hence `machinePurchaseUnits`, `priceCurrencies` and the cards'
// texts apart (`compare/compareEntries.js`).

const headers = { tenant: true, authorization: true }

/**
 * The kinds the reader may see prices of here, materials first: as the server asks it, the kind's records and their
 * purchase prices (`itemKindRights`); a kind without both cannot be compared. Non-reactive, for the palette.
 */
export const insightKinds = () =>
  ["MATERIAL", "MACHINE"].filter(kind => itemKindRights(kind).view.value && itemKindRights(kind).prices.value)

/** Reactive {@link insightKinds}. */
export const useInsightKinds = () => computed(insightKinds)

/**
 * `{ suppliers, items, truncated }`: the two suppliers named and the items either carries — or, with none named,
 * every supplier in use ("Eigener Betrieb" marked `builtIn`) and the items at least two of them carry — each item
 * with all their rows.
 */
export const loadSupplierComparison = ({ supplierIds = [], itemKind, search }) =>
  apiClient
    .get("/supplier-insights/comparison", {
      params: {
        supplierIds: supplierIds.length ? supplierIds.join(",") : undefined,
        itemKind,
        search: search || undefined,
      },
      headers,
    })
    .then(res => res.data)

/**
 * Whether a way into a Vergleich is offered (the view switch, a record's "Vergleichen"): the workspace set up, the
 * reader with a kind's records and prices — `kind`'s, when one is named — and the add-on held, or, once it is
 * released, not held: its routes then lead to "Freischalten", where it is bought. While it is unreleased that page
 * could only say "not available yet", so a workspace without it is offered nothing.
 */
export function useComparisonOffered(kind = null) {
  const strict = useSupplierStrict()
  const kinds = useInsightKinds()
  return computed(
    () =>
      strict.value &&
      (kind ? kinds.value.includes(kind) : kinds.value.length > 0) &&
      (!isUnreleased(FEATURE_SETS.SUPPLIER_INSIGHTS) || isEntitled(FEATURE_SETS.SUPPLIER_INSIGHTS))
  )
}

/**
 * Whether the server takes `iso` as a day of a period or a price list's "gilt ab": a real `YYYY-MM-DD` within
 * 2000-01-01..2100-12-31. Anything else it refuses as INVALID_FIELD, which reaches Sentry as a client bug.
 */
export function isServerDay(iso) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso ?? "") || iso < "2000-01-01" || iso > "2100-12-31") return false
  // "2026-02-30" has the shape but is no day; "2026-13-01" is not even a date (toISOString would throw).
  const date = new Date(`${iso}T00:00:00Z`)
  return !Number.isNaN(date.getTime()) && date.toISOString().startsWith(iso)
}

// Prices on screen have four decimals at most (`discounted`); a delivered price may carry float noise, which must
// not decide a tie.
const comparable = value => Math.round(value * 1e4) / 1e4

// An archived supplier's row (docs/suppliers.md, "Archiving a supplier"): shown, never compared — it is no offer.
// The Einkauf's rows say it of their supplier, a comparison's offers of themselves.
const isArchivedOffer = offer => Boolean(offer.supplier?.archived || offer.supplierArchived)

/** The currency most of the offers' suppliers in use have a price in, the workspace's first among equals. */
function offersCurrency(offers, workspaceCurrency) {
  const counts = {}
  const bySupplier = {}
  offers
    .filter(offer => !isArchivedOffer(offer))
    .forEach(offer =>
      currenciesOf(offer.prices).forEach(currency => (bySupplier[offer.supplierId] ??= new Set()).add(currency))
    )
  Object.values(bySupplier).forEach(currencies =>
    currencies.forEach(currency => (counts[currency] = (counts[currency] ?? 0) + 1))
  )
  // A stable sort: among equals the order `currenciesOf` gives.
  return currenciesOf(counts, workspaceCurrency).sort((a, b) => counts[b] - counts[a])[0] ?? null
}

/** One offer's price in a currency, less the supplier's discount; null without one there. */
function offerPrice(offer, conditions, currency) {
  const base = tierPrice(offer.prices, offer.tiers, null, currency)
  return base == null ? null : discounted(base, conditions?.discountPercent)
}

/**
 * The unit a machine's offers are compared in (docs/suppliers.md, "Einheit je Preiszeile"): each bills per its own unit
 * or the machine's (`itemUnit`); Tag, Woche and Monat compare per day, Stunde only with Stunde. The default's unit
 * leads, else the machine's, else the first offer's. Null for a material, whose offers name no unit.
 */
function offersUnit(offers, itemUnits) {
  const units = offers.map(offer => rowUnitOf(offer, itemUnits(offer)))
  if (!units.some(Boolean)) return null
  const preferred = offers.find(offer => offer.isDefault)
  return comparisonUnit(units, preferred ? rowUnitOf(preferred, itemUnits(preferred)) : units.find(Boolean))
}

/**
 * An offer priced in the item's currency; where it has none there, in its own first currency and not compared. With
 * a `unit` (a machine's) the figure compared is `basis`, the price in that unit; an offer that does not convert into it
 * (Stunde against Tag) is shown and not compared (`otherUnit`). An archived supplier's is shown and never compared
 * (`archived`).
 */
function pricedOffer(offer, conditions, currency, workspaceCurrency, unit = null, itemUnit = null) {
  const archived = isArchivedOffer(offer)
  const value = currency ? offerPrice(offer, conditions, currency) : null
  if (value != null) return inUnit({ offer, value, currency, comparable: !archived, archived }, unit, itemUnit)
  const own = currenciesOf(offer.prices, workspaceCurrency)[0]
  const other = own ? offerPrice(offer, conditions, own) : null
  if (other != null)
    return inUnit(
      { offer, value: other, currency: own, comparable: false, otherCurrency: true, archived },
      unit,
      itemUnit
    )
  return { offer, value: null, basis: null, currency: null, comparable: false, noPrice: true, archived }
}

function inUnit(candidate, unit, itemUnit) {
  const own = rowUnitOf(candidate.offer, itemUnit)
  if (!unit) return { ...candidate, unit: own, basis: candidate.value }
  const basis = convertUnitPrice(candidate.value, own, candidate.offer.daysPerUnit, unit)
  if (basis == null) return { ...candidate, unit: own, basis: null, comparable: false, otherUnit: true }
  return { ...candidate, unit: own, basis, converted: own !== unit }
}

/**
 * The Vergleich (docs/suppliers.md, "Vergleich"): the items two suppliers both carry, one row each. Every offer is
 * priced less its supplier's discount (the base price, below the first tier) in the currency most of the item's suppliers
 * have a price in, and the cheapest marked; per item its default against the cheapest. `supplierIds` keeps those
 * suppliers' offers only; an item left with fewer than `minSuppliers` suppliers is left out. A tie goes to the
 * default. A machine's offers compare in one unit (`offersUnit`): a week's price per day beside a day's.
 *
 * @param items the comparison's `items`
 * @param suppliers the comparison's `suppliers`, `{ id, conditions }`
 * @returns `[{ item, currency, unit, offers, standard, cheapest, saving, savingPercent }]` — `offers` each `{ offer,
 *   value, basis, unit, currency, comparable, otherCurrency, otherUnit, archived, converted, noPrice, cheapest }`
 *   (`value` in the offer's own unit, `basis` in the item's `unit`); `standard` (the default, if one of them) and
 *   `cheapest` two of them or null; `saving` what the default costs more per unit, 0 where it is the cheapest, null
 *   without both
 */
export function compareItems(items, suppliers, { workspaceCurrency = null, supplierIds = [], minSuppliers = 2 } = {}) {
  const conditionsOf = Object.fromEntries(suppliers.map(supplier => [supplier.id, supplier.conditions ?? null]))
  const chosen = supplierIds.length ? new Set(supplierIds) : null
  return (items ?? []).flatMap(item => {
    const offers = item.offers.filter(offer => !chosen || chosen.has(offer.supplierId))
    if (!offers.length || new Set(offers.map(offer => offer.supplierId)).size < minSuppliers) return []
    const currency = offersCurrency(offers, workspaceCurrency)
    // A material's unit is free text and the same for all its offers: only a machine's are compared by unit.
    const itemUnit = item.itemKind === "MACHINE" ? (item.unit ?? null) : null
    const unit = offersUnit(offers, () => itemUnit)
    const priced = offers.map(offer =>
      pricedOffer(offer, conditionsOf[offer.supplierId], currency, workspaceCurrency, unit, itemUnit)
    )
    const compared = priced.filter(candidate => candidate.comparable)
    const lowest = compared.length ? Math.min(...compared.map(candidate => comparable(candidate.basis))) : null
    const isLowest = candidate => candidate.comparable && comparable(candidate.basis) === lowest
    const standard = priced.find(candidate => candidate.offer.isDefault) ?? null
    const base = standard?.comparable ? comparable(standard.basis) : null
    const cheapest = standard && isLowest(standard) ? standard : (compared.find(isLowest) ?? null)
    const saving = base != null && cheapest ? base - comparable(cheapest.basis) : null
    priced.forEach(candidate => (candidate.cheapest = compared.length > 1 && isLowest(candidate)))
    return [
      {
        item,
        currency,
        unit,
        offers: priced,
        standard,
        cheapest,
        saving,
        savingPercent: saving > 0 ? (saving / base) * 100 : null,
      },
    ]
  })
}

/**
 * The two suppliers that share the most items, for a comparison that opens with something to compare; ties go to the
 * earlier pair in `suppliers`' order (by name). "Eigener Betrieb" is never one: the question is where to buy. Null
 * with fewer than two that share one.
 */
export function bestPair(items, suppliers) {
  const ids = suppliers.filter(supplier => !supplier.builtIn).map(supplier => supplier.id)
  const shared = {}
  ;(items ?? []).forEach(item => {
    const carriers = ids.filter(id => item.offers.some(offer => offer.supplierId === id))
    carriers.forEach((a, i) =>
      carriers.slice(i + 1).forEach(b => (shared[`${a}|${b}`] = (shared[`${a}|${b}`] ?? 0) + 1))
    )
  })
  let best = null
  ids.forEach((a, i) =>
    ids.slice(i + 1).forEach(b => {
      const count = shared[`${a}|${b}`] ?? 0
      if (count > 0 && (!best || count > best.count)) best = { ids: [a, b], count }
    })
  )
  return best?.ids ?? null
}

/**
 * The supplier that shares the most items with `supplierId`, to stand beside it when only it was named (its page's
 * "Vergleichen"); ties go to the earlier one in `suppliers`' order (by name), "Eigener Betrieb" never. Null when it
 * shares none.
 */
export function bestPartner(items, suppliers, supplierId) {
  const shared = {}
  ;(items ?? [])
    .filter(item => item.offers.some(offer => offer.supplierId === supplierId))
    .forEach(item =>
      new Set(item.offers.map(offer => offer.supplierId)).forEach(id => (shared[id] = (shared[id] ?? 0) + 1))
    )
  let best = null
  suppliers
    .filter(supplier => !supplier.builtIn && supplier.id !== supplierId)
    .forEach(supplier => {
      const count = shared[supplier.id] ?? 0
      if (count > 0 && (!best || count > best.count)) best = { id: supplier.id, count }
    })
  return best?.id ?? null
}

/**
 * Two items side by side (docs/suppliers.md, "Artikel vergleichen"), from their Einkauf rows (`purchase-overview`):
 * per supplier both are bought from, each item's rows there priced less that supplier's discount, in the currency
 * most of their suppliers have a price in, the cheaper marked — only where both are in one unit (`sameUnit`):
 * 128 EUR a m³ is not cheaper than 130 EUR a t. Two machines' rows compare in one unit as the Vergleich's do (a week
 * per day beside a day; `itemUnits` the machines' own, for rows without one). An archived supplier's rows are shown and
 * never compared, so never the cheaper. "Günstiger bei" counts the suppliers where an item is strictly the cheaper.
 * The suppliers only one of them is bought from are listed apart, unmarked.
 *
 * @param rows `[rowsOfTheFirst, rowsOfTheSecond]`, as `purchase-overview` answers them
 * @returns `{ currency, unit, shared: [{ supplier, sides: [priced[], priced[]] }], only: [[{ supplier, priced }], …],
 *   ahead: [n, n], scored }` — `priced` as `compareItems`' offers, by supplier name
 */
export function compareTwoItems(rows, { workspaceCurrency = null, sameUnit = true, itemUnits = [null, null] } = {}) {
  const offersOf = (list, side) =>
    (list ?? []).filter(row => row.supplier).map(row => ({ ...row, supplierId: row.supplier.id, side }))
  const offers = rows.map(offersOf)
  const currency = offersCurrency(offers.flat(), workspaceCurrency)
  const unitOfSide = offer => itemUnits[offer.side] ?? null
  // The first card's default leads the unit, as the default leads a Vergleich's row.
  const unit = offersUnit(offers.flat(), unitOfSide)
  const suppliersOf = list => new Map(list.map(offer => [offer.supplierId, offer.supplier]))
  const [first, second] = offers.map(suppliersOf)
  const byName = (a, b) => a.name.localeCompare(b.name)
  const price = offer =>
    pricedOffer(offer, offer.supplier.conditions ?? null, currency, workspaceCurrency, unit, unitOfSide(offer))

  const shared = [...first.values()]
    .filter(supplier => second.has(supplier.id))
    .sort(byName)
    .map(supplier => {
      const sides = offers.map(list => list.filter(offer => offer.supplierId === supplier.id).map(price))
      const compared = sides.flat().filter(candidate => candidate.comparable)
      const lowest = compared.length ? Math.min(...compared.map(candidate => comparable(candidate.basis))) : null
      const marked = sameUnit && sides.every(side => side.some(candidate => candidate.comparable))
      sides
        .flat()
        .forEach(
          candidate => (candidate.cheapest = marked && candidate.comparable && comparable(candidate.basis) === lowest)
        )
      return { supplier, sides, scored: marked }
    })
  const ahead = [0, 1].map(
    index =>
      shared.filter(
        row =>
          row.scored &&
          row.sides[index].some(candidate => candidate.cheapest) &&
          !row.sides[1 - index].some(candidate => candidate.cheapest)
      ).length
  )
  const only = [0, 1].map(index =>
    [...[first, second][index].values()]
      .filter(supplier => ![first, second][1 - index].has(supplier.id))
      .sort(byName)
      .map(supplier => ({
        supplier,
        priced: offers[index].filter(offer => offer.supplierId === supplier.id).map(price),
      }))
  )
  return { currency, unit, shared, only, ahead, scored: shared.filter(row => row.scored).length }
}
